import {
  Dispatch,
  PropsWithChildren,
  useEffect,
  useMemo,
  useReducer,
} from "react";
import { Operator } from "../../domains/Operator";
import { useCurrentOperatorApi } from "../../hooks/api/useCurrentOperatorApi";
import { ErrorView } from "../ErrorView";
import { UnauthorizedError } from "../../helpers/api";
import { useSignOutApi } from "../../hooks/api/useSignOutApi";
import {
  AuthenticationContext,
  AuthenticationStatus,
  makeAuthenticationContext,
} from "./AuthenticationContext";
import { useFlashMessage } from "../FlashMessage";

type State = {
  currentOperator: Operator | null;
  status: AuthenticationStatus;
};
type Actions =
  | {
      type: "START_AUTHENTICATION";
    }
  | {
      type: "COMPLETE_AUTHENTICATION";
      currentOperator: Operator;
    }
  | {
      type: "UNAUTHENTICATED";
    }
  | {
      type: "COMPLETE_SIGN_OUT";
    };

const authenticationReducer = (state: State, action: Actions): State => {
  switch (action.type) {
    case "START_AUTHENTICATION": {
      return { ...state, status: "authenticating" };
    }
    case "COMPLETE_AUTHENTICATION": {
      const { currentOperator } = action;
      return { ...state, currentOperator, status: "authenticated" };
    }
    case "UNAUTHENTICATED": {
      return { ...state, currentOperator: null, status: "unauthenticated" };
    }
    case "COMPLETE_SIGN_OUT": {
      return { ...state, currentOperator: null, status: "unauthenticated" };
    }
  }
};

type Props = PropsWithChildren<Record<string, unknown>>;
export const AuthenticationProvider = ({ children }: Props) => {
  const [state, dispatch] = useReducer(authenticationReducer, {
    currentOperator: null,
    status: "initialized",
  });

  const {
    currentOperator,
    isLoading: isLoadingUserProfile,
    error,
  } = useCurrentOperatorApi();

  // 認証関連処理
  useEffect(() => {
    if (isLoadingUserProfile) {
      dispatch({ type: "START_AUTHENTICATION" });
    } else if (currentOperator) {
      dispatch({
        type: "COMPLETE_AUTHENTICATION",
        currentOperator: currentOperator,
      });
    }
  }, [isLoadingUserProfile, currentOperator]);
  useEffect(() => {
    if (error instanceof UnauthorizedError) {
      dispatch({ type: "UNAUTHENTICATED" });
    }
  }, [error]);

  const signOut = useSignOut(state, dispatch);

  const context = useMemo(() => {
    return makeAuthenticationContext({
      currentOperator: state.currentOperator,
      status: state.status,
      signOut,
    });
  }, [state.currentOperator, state.status, signOut]);

  if (error && !(error instanceof UnauthorizedError)) {
    return <ErrorView error={error} />;
  }

  return (
    <AuthenticationContext.Provider value={context}>
      {children}
    </AuthenticationContext.Provider>
  );
};

const useSignOut = (state: State, dispatch: Dispatch<Actions>) => {
  const { showMessage } = useFlashMessage();

  // サインアウト関連処理
  const { mutate: signOut } = useSignOutApi({
    onSuccess: () => {
      dispatch({ type: "COMPLETE_SIGN_OUT" });
    },
    onError: () => {
      showMessage("error", "ログアウトできませんでした。");
    },
  });

  return signOut;
};
